/**
 * Shared notes/scratchpad surface for simulation modules.
 *
 * Unifies the Listening scratchpad and the Writing prompt-panel notes area
 * behind one primitive. Two variants are supported:
 *
 *  - `full`: full-height panel with title bar, optional Clear action with
 *    confirmation dialog, and a tip footer. Used by Listening.
 *  - `compact`: embedded label + textarea with optional clear action. Used
 *    inside resizable prompt/context panels.
 *
 * The component is locally stateful only for the Clear confirmation dialog.
 * All persisted note text is owned by the parent via `notes` / `onNotesChange`.
 *
 * Surface tokens: outer container expects to sit on `bg-exam-notes` or an
 * equivalent neutral surface; the parent chooses.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FileText, Trash2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

export type NotesPanelVariant = "full" | "compact";

export interface NotesPanelProps {
  /** Current notes value (owned by parent). */
  readonly notes: string;
  /** Called whenever the learner types in the notes area. */
  readonly onNotesChange: (notes: string) => void;
  /** Variant of the panel — `full` for standalone tabs, `compact` for embedded pane. */
  readonly variant?: NotesPanelVariant;
  /** Optional title override (defaults to `simulation.notes.title`). */
  readonly title?: string;
  /** Optional tip override (defaults to `simulation.notes.tip`). */
  readonly tip?: string;
  /** Placeholder text inside the textarea. */
  readonly placeholder?: string;
  /** Whether to show the "Not graded" badge next to the title. */
  readonly showNotGraded?: boolean;
  /** Whether to show the Clear button (with confirmation dialog). */
  readonly showClear?: boolean;
  /** Extra className appended to the outer container. */
  readonly className?: string;
}

export function NotesPanel({
  notes,
  onNotesChange,
  variant = "full",
  title,
  tip,
  placeholder,
  showNotGraded = true,
  showClear = true,
  className,
}: NotesPanelProps) {
  const { t } = useTranslation();
  const [showClearDialog, setShowClearDialog] = useState(false);

  const resolvedTitle =
    title ?? t("simulation.notes.title", { defaultValue: "Your Notes" });
  const resolvedTip =
    tip ??
    t("simulation.notes.tip", {
      defaultValue:
        "💡 Tip: Jot down key words, numbers, and names as you hear them.",
    });
  const resolvedPlaceholder =
    placeholder ??
    t("simulation.notes.placeholder", {
      defaultValue:
        "Type quick notes here as you listen... These won't be graded.",
    });

  const handleConfirmClear = () => {
    onNotesChange("");
    setShowClearDialog(false);
  };

  if (variant === "compact") {
    return (
      <div
        className={cn(
          "flex h-full min-h-0 flex-col gap-2 overflow-hidden bg-muted/10 px-3 pb-3 pt-2.5 sm:px-4 sm:pb-4 sm:pt-3",
          className,
        )}
      >
        <div className="flex items-center justify-between gap-2 shrink-0">
          <p className="font-ui text-[10px] font-bold uppercase tracking-tight text-muted-foreground">
            {resolvedTitle}
          </p>
          {showClear && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowClearDialog(true)}
              className="h-6 px-1.5 text-[10px] text-muted-foreground hover:text-foreground hover:bg-destructive/10"
            >
              <Trash2 className="w-3 h-3 mr-1" />
              {t("simulation.notes.clear", { defaultValue: "Clear" })}
            </Button>
          )}
        </div>
        <Textarea
          value={notes}
          onChange={(e) => onNotesChange(e.target.value)}
          placeholder={resolvedPlaceholder}
          className="min-h-0 flex-1 resize-none border-dashed bg-muted/30 font-ui text-base lg:text-sm"
          aria-label={t("simulation.writing.prompt.notesAriaLabel", {
            defaultValue: "Notes scratchpad",
          })}
          autoComplete="off"
          spellCheck={false}
        />
        <AlertDialog open={showClearDialog} onOpenChange={setShowClearDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("simulation.notes.clearTitle", {
                  defaultValue: "Clear all notes?",
                })}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t("simulation.notes.clearDetail", {
                  defaultValue:
                    "This will permanently delete all your notes. This action cannot be undone.",
                })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>
                {t("simulation.notes.clearCancel", { defaultValue: "Cancel" })}
              </AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirmClear}>
                {t("simulation.notes.clearConfirm", { defaultValue: "Clear" })}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  return (
    <>
      <div
        className={cn(
          "h-full flex flex-col bg-card lg:border-r border-border",
          className,
        )}
      >
        {/* Header */}
        <div className="h-10 sm:h-12 border-b border-border flex items-center justify-between px-3 sm:px-4 shrink-0 bg-muted/10">
          <div className="flex items-center gap-2">
            <FileText className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="font-ui text-xs sm:text-sm font-bold text-foreground uppercase tracking-tight">
              {resolvedTitle}
            </span>
            {showNotGraded && (
              <span className="font-ui text-[10px] text-muted-foreground hidden sm:inline">
                {t("simulation.notes.notGraded", {
                  defaultValue: "(Not Graded)",
                })}
              </span>
            )}
          </div>
          {showClear && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowClearDialog(true)}
              className="h-7 px-2 text-[10px] sm:text-xs text-muted-foreground hover:text-foreground hover:bg-destructive/10"
            >
              <Trash2 className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1" />
              {t("simulation.notes.clear", { defaultValue: "Clear" })}
            </Button>
          )}
        </div>

        {/* Notes Area */}
        <div className="flex-1 p-3 sm:p-4">
          <Textarea
            value={notes}
            onChange={(e) => onNotesChange(e.target.value)}
            placeholder={resolvedPlaceholder}
            className="h-full resize-none text-sm sm:text-base bg-muted/20 border-dashed leading-relaxed focus:bg-background transition-colors"
          />
        </div>

        {/* Tip Footer */}
        <div className="px-3 py-2 sm:p-3 border-t border-border bg-muted/20">
          <p className="font-ui text-[10px] sm:text-xs text-muted-foreground leading-tight">
            {resolvedTip}
          </p>
        </div>
      </div>

      <AlertDialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("simulation.notes.clearTitle", {
                defaultValue: "Clear all notes?",
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("simulation.notes.clearDetail", {
                defaultValue:
                  "This will permanently delete all your notes. This action cannot be undone.",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t("simulation.notes.clearCancel", { defaultValue: "Cancel" })}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmClear}>
              {t("simulation.notes.clearConfirm", { defaultValue: "Clear" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
